import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  ArrowLeft, Save, ChevronLeft, ChevronRight, Trash2, Loader2,
  User, Mail, Building, PenTool, Calendar, Type, GripVertical,
  CheckSquare, Fingerprint, ThumbsUp, DollarSign, Plus, X, Users,
  UserCheck, Shield
} from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Colors per signer type
const CLIENT_COLORS = [
  { color: "#3b82f6", bg: "#eff6ff" },
  { color: "#e11d48", bg: "#fff1f2" },
  { color: "#d97706", bg: "#fffbeb" },
  { color: "#7c3aed", bg: "#f5f3ff" },
];
const ADVISOR_STYLE = { color: "#16a34a", bg: "#f0fdf4" };
const ROLE_STYLE = { color: "#0891b2", bg: "#ecfeff" };

// Signer-specific field types (used by clients, advisor, additional roles)
const SIGNER_FIELD_TYPES = [
  { type: "first_name", label: "First Name", icon: User },
  { type: "last_name", label: "Last Name", icon: User },
  { type: "email", label: "Email", icon: Mail },
  { type: "signature", label: "Signature", icon: PenTool },
  { type: "initials", label: "Initials", icon: Fingerprint },
  { type: "date_signed", label: "Date Signed", icon: Calendar },
  { type: "checkbox", label: "Checkbox", icon: CheckSquare },
  { type: "approve", label: "Approve", icon: ThumbsUp },
];

// Global field types (not tied to any signer)
const GLOBAL_FIELD_TYPES = [
  { type: "advisor_name", label: "Advisor Name", icon: User, color: "#16a34a", bg: "#f0fdf4" },
  { type: "advisor_company", label: "Advisor Company", icon: Building, color: "#16a34a", bg: "#f0fdf4" },
  { type: "fee_amount", label: "Fee Amount", icon: DollarSign, color: "#ca8a04", bg: "#fefce8" },
  { type: "custom_text", label: "Custom Text", icon: Type, color: "#64748b", bg: "#f8fafc" },
];

const DEFAULT_SIZES = {
  first_name: { width: 20, height: 3.5 },
  last_name: { width: 20, height: 3.5 },
  email: { width: 24, height: 3.5 },
  signature: { width: 20, height: 6 },
  initials: { width: 10, height: 4 },
  date_signed: { width: 16, height: 3.5 },
  checkbox: { width: 4, height: 3.5 },
  approve: { width: 14, height: 4 },
  advisor_name: { width: 22, height: 3.5 },
  advisor_company: { width: 22, height: 3.5 },
  fee_amount: { width: 18, height: 3.5 },
  custom_text: { width: 22, height: 3.5 },
};

const genId = () => `fld_${Math.random().toString(36).slice(2, 10)}`;

// Get color for a field based on its signer assignment
const getFieldColor = (field, signers) => {
  if (!field.signer) {
    const g = GLOBAL_FIELD_TYPES.find(f => f.type === field.type);
    return g || { color: "#64748b", bg: "#f8fafc" };
  }
  const s = signers.find(sg => sg.id === field.signer);
  if (!s) return { color: "#64748b", bg: "#f8fafc" };
  if (s.type === "advisor") return ADVISOR_STYLE;
  if (s.type === "additional") return ROLE_STYLE;
  // Client — use index-based color
  const clientList = signers.filter(sg => sg.type === "client" || (!sg.type && typeof sg.id === "number"));
  const idx = clientList.findIndex(sg => sg.id === s.id);
  return CLIENT_COLORS[Math.max(0, idx) % CLIENT_COLORS.length];
};

// Get display label for a placed field
const getFieldLabel = (field, signers) => {
  if (field.type === "custom_text" && field.custom_label) return field.custom_label;
  const base = [...SIGNER_FIELD_TYPES, ...GLOBAL_FIELD_TYPES].find(f => f.type === field.type)?.label || field.type;
  if (!field.signer) return base;
  const s = signers.find(sg => sg.id === field.signer);
  if (!s) return base;
  // Short prefix
  let prefix;
  if (s.type === "advisor") prefix = "Adv";
  else if (s.type === "additional") prefix = s.short || s.label.substring(0, 3);
  else {
    const clientSigners = signers.filter(sg => sg.type === "client");
    const idx = clientSigners.findIndex(sg => sg.id === s.id);
    prefix = `C${idx + 1}`;
  }
  return `${prefix}: ${base}`;
};

const FieldMapper = () => {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [docMeta, setDocMeta] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [fields, setFields] = useState([]);
  const [signers, setSigners] = useState([
    { id: 1, label: "Client 1", type: "client" },
    { id: "advisor", label: "Advisor", type: "advisor" },
  ]);
  const [activeSigner, setActiveSigner] = useState(1);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");

  const overlayRef = useRef(null);
  const dragRef = useRef(null);

  const clientSigners = signers.filter(s => s.type === "client");
  const advisorSigner = signers.find(s => s.type === "advisor");
  const additionalSigners = signers.filter(s => s.type === "additional");
  const activeSignerObj = signers.find(s => s.id === activeSigner);

  // Load document
  useEffect(() => {
    const load = async () => {
      try {
        const [metaRes, pdfRes, mapRes] = await Promise.all([
          axios.get(`${API}/docusign/documents`, { headers, withCredentials: true }),
          axios.get(`${API}/docusign/documents/${docId}/preview`, { headers, responseType: "blob", withCredentials: true }),
          axios.get(`${API}/docusign/documents/${docId}/mappings`, { headers, withCredentials: true }),
        ]);
        setDocMeta(metaRes.data.find(d => d.doc_id === docId));
        setPdfUrl(URL.createObjectURL(pdfRes.data));
        if (mapRes.data.fields?.length) setFields(mapRes.data.fields);
        if (mapRes.data.signers?.length) {
          // Migrate old signers: add type if missing, ensure advisor exists
          const loaded = mapRes.data.signers.map(s => {
            if (s.type) return s;
            if (s.id === "advisor") return { ...s, type: "advisor" };
            if (typeof s.id === "string" && s.id.startsWith("role_")) return { ...s, type: "additional" };
            return { ...s, type: "client" };
          });
          if (!loaded.find(s => s.type === "advisor")) {
            loaded.push({ id: "advisor", label: "Advisor", type: "advisor" });
          }
          setSigners(loaded);
        }
      } catch {
        toast.error("Failed to load document");
        navigate("/settings?tab=documents");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  // Get active signer style for palette icons
  const getActiveStyle = () => {
    if (!activeSignerObj) return CLIENT_COLORS[0];
    if (activeSignerObj.type === "advisor") return ADVISOR_STYLE;
    if (activeSignerObj.type === "additional") return ROLE_STYLE;
    const idx = clientSigners.findIndex(s => s.id === activeSignerObj.id);
    return CLIENT_COLORS[idx % CLIENT_COLORS.length];
  };

  const activeStyle = getActiveStyle();

  // Drag
  const startDrag = useCallback((e, fieldId) => {
    e.stopPropagation();
    const rect = overlayRef.current.getBoundingClientRect();
    const field = fields.find(f => f.field_id === fieldId);
    if (!field) return;
    const startX = e.clientX, startY = e.clientY;
    const origX = field.x, origY = field.y;
    dragRef.current = { didDrag: false };
    const onMove = (me) => {
      const dx = ((me.clientX - startX) / rect.width) * 100;
      const dy = ((me.clientY - startY) / rect.height) * 100;
      if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3) dragRef.current.didDrag = true;
      setFields(prev => prev.map(f => f.field_id === fieldId
        ? { ...f, x: Math.max(0, Math.min(origX + dx, 100 - f.width)), y: Math.max(0, Math.min(origY + dy, 100 - f.height)) }
        : f
      ));
      setDirty(true);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setTimeout(() => { dragRef.current = null; }, 50);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    setSelectedField(fieldId);
  }, [fields]);

  const deleteField = useCallback((fieldId) => {
    setFields(prev => prev.filter(f => f.field_id !== fieldId));
    if (selectedField === fieldId) setSelectedField(null);
    setDirty(true);
  }, [selectedField]);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if ((e.key === "Delete" || e.key === "Backspace") && selectedField && !e.target.closest("input")) deleteField(selectedField);
      if (e.key === "Escape") { setSelectedField(null); setSelectedTool(null); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedField, deleteField]);

  // Save
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/docusign/documents/${docId}/mappings`, { fields, signers }, { headers, withCredentials: true });
      toast.success(`Saved ${fields.length} field${fields.length !== 1 ? "s" : ""}`);
      setDirty(false);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  // Signer management
  const addClient = () => {
    if (clientSigners.length >= 4) return;
    const nextNum = clientSigners.length + 1;
    const nextId = Math.max(0, ...clientSigners.map(s => s.id)) + 1;
    setSigners(prev => [...prev, { id: nextId, label: `Client ${nextNum}`, type: "client" }]);
    setActiveSigner(nextId);
    setDirty(true);
  };

  const removeClient = (clientId) => {
    if (clientSigners.length <= 1) return;
    setSigners(prev => prev.filter(s => s.id !== clientId));
    setFields(prev => prev.filter(f => f.signer !== clientId));
    if (activeSigner === clientId) setActiveSigner(clientSigners.find(s => s.id !== clientId)?.id || "advisor");
    setDirty(true);
  };

  const addAdditionalRole = () => {
    if (!newRoleName.trim()) return;
    const roleId = `role_${Date.now()}`;
    setSigners(prev => [...prev, { id: roleId, label: newRoleName.trim(), short: newRoleName.trim().substring(0, 3).toUpperCase(), type: "additional" }]);
    setActiveSigner(roleId);
    setNewRoleName("");
    setAddingRole(false);
    setDirty(true);
  };

  const removeAdditionalRole = (roleId) => {
    setSigners(prev => prev.filter(s => s.id !== roleId));
    setFields(prev => prev.filter(f => f.signer !== roleId));
    if (activeSigner === roleId) setActiveSigner(1);
    setDirty(true);
  };

  // Custom text properties
  const updateFieldProp = (fieldId, props) => {
    setFields(prev => prev.map(f => {
      if (f.field_id !== fieldId) return f;
      const updated = { ...f, ...props };
      if (props.custom_label !== undefined) updated.label = props.custom_label || "Custom Text";
      return updated;
    }));
    setDirty(true);
  };

  const selectedFieldData = fields.find(f => f.field_id === selectedField);
  const pageFields = fields.filter(f => f.page === currentPage);

  if (loading) {
    return <div className="min-h-screen bg-stone/20 flex items-center justify-center"><Loader2 className="w-8 h-8 text-forest animate-spin" /></div>;
  }

  return (
    <div className="h-screen flex flex-col bg-[#1e1e1e] text-white overflow-hidden" data-testid="field-mapper-page">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#2d2d2d] border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10" onClick={() => navigate("/settings?tab=documents")} data-testid="back-to-documents">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <p className="text-sm font-medium text-white/90 leading-none">{docMeta?.display_name || docMeta?.filename || "Document"}</p>
            <p className="text-xs text-white/40 mt-0.5">Field Mapper</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {numPages && (
            <div className="flex items-center gap-1.5 mr-2">
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} data-testid="mapper-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs font-mono text-white/50 min-w-[60px] text-center">{currentPage} / {numPages}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))} disabled={currentPage >= numPages} data-testid="mapper-next-page"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
          <span className="text-xs text-white/40 mr-1">{fields.length} field{fields.length !== 1 ? "s" : ""}</span>
          <Button size="sm" className="bg-forest hover:bg-forest/90 text-white h-8 px-4" onClick={handleSave} disabled={saving || !dirty} data-testid="save-mappings-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            {saving ? "Saving..." : dirty ? "Save" : "Saved"}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-60 bg-[#2d2d2d] border-r border-white/10 flex flex-col shrink-0 overflow-y-auto" data-testid="field-palette">

          {/* === CLIENTS === */}
          <div className="p-3 border-b border-white/10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30 flex items-center gap-1"><Users className="w-3 h-3" /> Clients</p>
              {clientSigners.length < 4 && (
                <button onClick={addClient} className="text-white/40 hover:text-white" data-testid="add-client-btn"><Plus className="w-3.5 h-3.5" /></button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {clientSigners.map((s, i) => {
                const sc = CLIENT_COLORS[i % CLIENT_COLORS.length];
                const isActive = activeSigner === s.id;
                return (
                  <button key={s.id} onClick={() => setActiveSigner(s.id)}
                    className={`relative flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${isActive ? "ring-1 text-white" : "text-white/50 hover:text-white/70"}`}
                    style={{ backgroundColor: isActive ? sc.color + "25" : "transparent" }}
                    data-testid={`client-tab-${s.id}`}
                  >
                    <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: sc.color }}>C{i + 1}</span>
                    {s.label}
                    {clientSigners.length > 1 && isActive && (
                      <span onClick={(e) => { e.stopPropagation(); removeClient(s.id); }} className="ml-0.5 opacity-60 hover:opacity-100" data-testid={`remove-client-${s.id}`}><X className="w-3 h-3" /></span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* === ADVISOR === */}
          <div className="p-3 border-b border-white/10">
            <button
              onClick={() => setActiveSigner("advisor")}
              className={`flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-xs font-medium transition-all ${activeSigner === "advisor" ? "ring-1 text-white" : "text-white/50 hover:text-white/70"}`}
              style={{ backgroundColor: activeSigner === "advisor" ? ADVISOR_STYLE.color + "25" : "transparent" }}
              data-testid="advisor-tab"
            >
              <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: ADVISOR_STYLE.color }}>
                <UserCheck className="w-3 h-3 text-white" />
              </span>
              Advisor
              <span className="text-[9px] text-white/30 ml-auto">Signs last</span>
            </button>
          </div>

          {/* === ADDITIONAL ROLES === */}
          <div className="p-3 border-b border-white/10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30 flex items-center gap-1"><Shield className="w-3 h-3" /> Additional Roles</p>
              <button onClick={() => setAddingRole(true)} className="text-white/40 hover:text-white" data-testid="add-role-btn"><Plus className="w-3.5 h-3.5" /></button>
            </div>
            {addingRole && (
              <div className="flex gap-1 mb-2">
                <Input
                  autoFocus
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addAdditionalRole(); if (e.key === "Escape") { setAddingRole(false); setNewRoleName(""); } }}
                  placeholder="e.g. Compliance"
                  className="h-7 text-xs bg-white/5 border-white/10 text-white placeholder:text-white/20"
                  data-testid="new-role-input"
                />
              </div>
            )}
            {additionalSigners.length === 0 && !addingRole && (
              <p className="text-[10px] text-white/20 px-1">No additional roles yet</p>
            )}
            <div className="flex flex-col gap-1">
              {additionalSigners.map(s => {
                const isActive = activeSigner === s.id;
                return (
                  <button key={s.id} onClick={() => setActiveSigner(s.id)}
                    className={`flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${isActive ? "ring-1 text-white" : "text-white/50 hover:text-white/70"}`}
                    style={{ backgroundColor: isActive ? ROLE_STYLE.color + "25" : "transparent" }}
                    data-testid={`role-tab-${s.id}`}
                  >
                    <span className="w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: ROLE_STYLE.color }}>
                      <Shield className="w-2.5 h-2.5 text-white" />
                    </span>
                    <span className="truncate">{s.label}</span>
                    {isActive && (
                      <span onClick={(e) => { e.stopPropagation(); removeAdditionalRole(s.id); }} className="ml-auto opacity-60 hover:opacity-100" data-testid={`remove-role-${s.id}`}><X className="w-3 h-3" /></span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* === SIGNER FIELD PALETTE === */}
          <div className="p-3 border-b border-white/10">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30 mb-2 px-1">
              {activeSignerObj?.type === "advisor" ? "Advisor" : activeSignerObj?.type === "additional" ? activeSignerObj.label : activeSignerObj?.label || "Signer"} Fields
            </p>
            <div className="flex flex-col gap-0.5">
              {SIGNER_FIELD_TYPES.map(ft => {
                const Icon = ft.icon;
                const toolKey = `${ft.type}__signer`;
                const active = selectedTool === toolKey;
                return (
                  <button key={ft.type} onClick={() => setSelectedTool(active ? null : toolKey)}
                    className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md text-left text-sm transition-all ${active ? "bg-white/15 text-white ring-1 ring-white/20" : "text-white/60 hover:bg-white/5 hover:text-white/80"}`}
                    data-testid={`tool-${ft.type}`}
                  >
                    <div className="w-5 h-5 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: activeStyle.bg }}>
                      <Icon className="w-3 h-3" style={{ color: activeStyle.color }} />
                    </div>
                    <span className="truncate">{ft.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* === DOCUMENT (GLOBAL) FIELDS === */}
          <div className="p-3 border-b border-white/10">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30 mb-2 px-1">Document Fields</p>
            <div className="flex flex-col gap-0.5">
              {GLOBAL_FIELD_TYPES.map(ft => {
                const Icon = ft.icon;
                const active = selectedTool === ft.type;
                return (
                  <button key={ft.type} onClick={() => setSelectedTool(active ? null : ft.type)}
                    className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md text-left text-sm transition-all ${active ? "bg-white/15 text-white ring-1 ring-white/20" : "text-white/60 hover:bg-white/5 hover:text-white/80"}`}
                    data-testid={`tool-${ft.type}`}
                  >
                    <div className="w-5 h-5 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: ft.bg }}>
                      <Icon className="w-3 h-3" style={{ color: ft.color }} />
                    </div>
                    <span className="truncate">{ft.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom Text Properties */}
          {selectedFieldData?.type === "custom_text" && (
            <div className="p-3 border-b border-white/10" data-testid="custom-text-properties">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30 mb-2 px-1">Custom Field Properties</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-white/40">Label</Label>
                  <Input value={selectedFieldData.custom_label || ""} onChange={(e) => updateFieldProp(selectedField, { custom_label: e.target.value })} placeholder="e.g. Account Number" className="h-7 text-xs mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/20" data-testid="custom-label-input" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-white/40">Mode</Label>
                  <div className="flex gap-1 mt-1">
                    <button onClick={() => updateFieldProp(selectedField, { custom_mode: "prospect_fills" })} className={`flex-1 text-[10px] py-1.5 rounded-md font-medium transition-all ${selectedFieldData.custom_mode !== "prefilled" ? "bg-white/15 text-white" : "text-white/40 hover:text-white/60"}`} data-testid="mode-prospect-fills">Prospect Fills</button>
                    <button onClick={() => updateFieldProp(selectedField, { custom_mode: "prefilled" })} className={`flex-1 text-[10px] py-1.5 rounded-md font-medium transition-all ${selectedFieldData.custom_mode === "prefilled" ? "bg-white/15 text-white" : "text-white/40 hover:text-white/60"}`} data-testid="mode-prefilled">Pre-filled</button>
                  </div>
                </div>
                {selectedFieldData.custom_mode === "prefilled" && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-white/40">Value</Label>
                    <Input value={selectedFieldData.custom_value || ""} onChange={(e) => updateFieldProp(selectedField, { custom_value: e.target.value })} placeholder="Pre-filled text..." className="h-7 text-xs mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/20" data-testid="custom-value-input" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bottom */}
          <div className="p-3 mt-auto">
            <p className="text-[10px] text-white/25 px-1 leading-relaxed">
              {selectedTool ? "Click on the document to place." : "Select a field, then click on the document."}
            </p>
            {selectedField && (
              <Button variant="ghost" size="sm" className="w-full mt-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8" onClick={() => deleteField(selectedField)} data-testid="delete-selected-field">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete Selected
              </Button>
            )}
          </div>
        </div>

        {/* PDF Canvas */}
        <div className="flex-1 overflow-auto flex justify-center items-start p-8 bg-[#1a1a1a]">
          <div className="relative shadow-2xl" data-testid="pdf-canvas-wrapper">
            <Document file={pdfUrl} onLoadSuccess={({ numPages: n }) => setNumPages(n)} loading={<Loader2 className="w-8 h-8 text-white/50 animate-spin" />}>
              <Page pageNumber={currentPage} width={680} renderTextLayer={false} renderAnnotationLayer={false} />
            </Document>

            {/* Field Overlay */}
            <div
              ref={overlayRef}
              className={`absolute inset-0 ${selectedTool ? "cursor-crosshair" : ""}`}
              onClick={(e) => {
                if (!selectedTool || dragRef.current?.didDrag) return;
                const rect = overlayRef.current.getBoundingClientRect();
                const xPct = ((e.clientX - rect.left) / rect.width) * 100;
                const yPct = ((e.clientY - rect.top) / rect.height) * 100;
                const isSigner = selectedTool.endsWith("__signer");
                const actualType = isSigner ? selectedTool.replace("__signer", "") : selectedTool;
                const size = DEFAULT_SIZES[actualType];
                if (!size) return;

                const newField = {
                  field_id: genId(),
                  type: actualType,
                  signer: isSigner ? activeSigner : null,
                  page: currentPage,
                  x: Math.max(0, Math.min(xPct - size.width / 2, 100 - size.width)),
                  y: Math.max(0, Math.min(yPct - size.height / 2, 100 - size.height)),
                  width: size.width,
                  height: size.height,
                };
                newField.label = getFieldLabel(newField, signers);
                if (actualType === "custom_text") {
                  newField.custom_label = "";
                  newField.custom_mode = "prospect_fills";
                  newField.custom_value = "";
                }
                setFields(prev => [...prev, newField]);
                setSelectedField(newField.field_id);
                setDirty(true);
              }}
              data-testid="field-overlay"
            >
              {pageFields.map(field => {
                const fc = getFieldColor(field, signers);
                const isSelected = selectedField === field.field_id;
                return (
                  <div key={field.field_id}
                    className={`absolute group flex items-center select-none transition-shadow ${isSelected ? "ring-2 ring-white shadow-lg z-20" : "z-10 hover:ring-1 hover:ring-white/50"}`}
                    style={{ left: `${field.x}%`, top: `${field.y}%`, width: `${field.width}%`, height: `${field.height}%`, backgroundColor: fc.bg, borderLeft: `3px solid ${fc.color}`, borderRadius: "3px" }}
                    onMouseDown={(e) => startDrag(e, field.field_id)}
                    onClick={(e) => { e.stopPropagation(); setSelectedField(field.field_id); }}
                    data-testid={`placed-field-${field.field_id}`}
                  >
                    <div className="flex items-center gap-1 px-1.5 overflow-hidden w-full h-full">
                      <GripVertical className="w-3 h-3 shrink-0 opacity-40 cursor-grab" style={{ color: fc.color }} />
                      <span className="text-[10px] font-medium truncate" style={{ color: fc.color }}>{getFieldLabel(field, signers)}</span>
                    </div>
                    <button className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[8px] leading-none hover:bg-red-600"
                      onClick={(e) => { e.stopPropagation(); deleteField(field.field_id); }} data-testid={`delete-field-${field.field_id}`}>&times;</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldMapper;
